import { Injectable } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { JwtHelperServiceWrapper } from './jwt-helper-service-wrapper';
import { ConfigVaultService } from './config-vault.service';
import { environment } from 'src/environments/environment';

export interface ITaxDocNotifierJwt {
  read: boolean;
  upload: boolean;
  aud: string;
  exp: string;
  iat: string;
  iss: string;
  jti: string;
  nbf: string;
  sub: string;
}

export enum TaxDocNotifierClaim {
  Read = "read",
  Upload = "upload"
}

@Injectable({
  providedIn: 'root'
})
export class JwtService {
  private readonly jwtHelperService: JwtHelperService;

  constructor(
    private readonly configVaultService: ConfigVaultService,
    jwtHelperServiceWrapper: JwtHelperServiceWrapper,
  ) {
    this.jwtHelperService = jwtHelperServiceWrapper.getJwtHelperService();
  }

  getToken(): Observable<ITaxDocNotifierJwt> {
    return this.getRawToken()
      .pipe(map(rawToken => {
        return this.jwtHelperService.decodeToken(rawToken);
      }));
  }

  getRawToken(): Observable<string> {
    const token = this.getTokenFromCache();
    if (token !== null) { // have jwt
      return of(token);
    }

    return this.configVaultService.getJWT()
      .pipe(
        catchError(() => of('')),
        tap(jwt => this.cacheToken(jwt))
      );
  }

  private getTokenFromCache(): string | null {
    const jwt = sessionStorage.getItem(this.getTokenCacheKey());
    if (jwt !== null) { // have jwt
      if (!this.jwtHelperService.isTokenExpired(jwt)) {
        return jwt;
      }
    }
    return null;
  }

  private cacheToken(token: string): void {
    sessionStorage.setItem(this.getTokenCacheKey(), token);
  }

  private getTokenCacheKey(): string {
    const appName = environment.jwtConfig.appName;
    return `${appName}-Token`;
  }
}
